import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../ui/button";
import {
  trackCtaClick,
  trackPlaygroundInteraction,
} from "../../lib/analytics";

type CapturedRequest = {
  id: string;
  method: string;
  body: unknown;
  query: Record<string, string>;
  timestamp: string;
};

type MintResponse = { id: string; expiresAt: string };

const MAX_VISIBLE = 5;

export function PlaygroundDemo() {
  const [id, setId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [requests, setRequests] = useState<CapturedRequest[]>([]);
  const [copied, setCopied] = useState(false);
  const wsRef = useRef<WebSocket | null>(null);
  const mintedRef = useRef(false);

  useEffect(() => {
    // StrictMode double-effect guard — we don't want to mint twice and burn
    // the IP rate limit on the same pageview.
    if (mintedRef.current) return;
    mintedRef.current = true;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch("/api/playground/webhook", { method: "POST" });
        if (!res.ok) {
          const payload = await res.json().catch(() => ({}));
          throw new Error(
            (payload as { error?: string }).error ?? "Failed to start demo",
          );
        }
        const data = (await res.json()) as MintResponse;
        if (cancelled) return;
        setId(data.id);
        trackPlaygroundInteraction("mint");
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : "Failed to start demo");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!id) return;
    const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
    const ws = new WebSocket(
      `${protocol}//${window.location.host}/ws/playground/${id}`,
    );
    wsRef.current = ws;
    ws.onopen = () => trackPlaygroundInteraction("ws_connected");
    ws.onmessage = (e) => {
      try {
        const captured = JSON.parse(e.data as string) as CapturedRequest;
        setRequests((prev) => [captured, ...prev].slice(0, MAX_VISIBLE));
        trackPlaygroundInteraction("request_seen");
      } catch {
        // ignore
      }
    };
    return () => {
      ws.close();
      wsRef.current = null;
    };
  }, [id]);

  const ingestUrl = id
    ? `${window.location.origin}/api/playground/ingest/${id}`
    : "";
  const curlSnippet = id
    ? `curl -X POST ${ingestUrl} \\\n  -H 'Content-Type: application/json' \\\n  -d '{"hello":"from the campaign"}'`
    : "";

  async function copyCurl() {
    if (!curlSnippet) return;
    try {
      await navigator.clipboard.writeText(curlSnippet);
      setCopied(true);
      trackPlaygroundInteraction("url_copied");
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // clipboard may be blocked in some browsers — silently fail
    }
  }

  return (
    <section id="playground" className="border-t border-b border-rule">
      <div className="max-w-7xl mx-auto w-full px-5 sm:px-12 py-12 sm:py-16">
        <div className="grid lg:grid-cols-[1fr_1.2fr] gap-10">
          <div>
            <span className="text-eyebrow text-signal-red">
              ∗ No signup, no payload limit lies
            </span>
            <h2 className="mt-4 text-3xl sm:text-4xl font-semibold tracking-[-0.03em]">
              Curl it now.{" "}
              <span className="font-serif italic font-normal">
                Watch it land.
              </span>
            </h2>
            <p className="mt-3 text-base leading-relaxed text-muted-foreground max-w-md">
              We just minted you a private webhook inbox for the next hour. Paste
              the curl into a terminal — the request shows up in the panel on
              the right in real time. That's the whole product, no signup
              required.
            </p>

            <div className="mt-6 rounded-lg border border-rule bg-card-paper overflow-hidden">
              <div className="px-4 py-2.5 border-b border-rule-soft flex items-center justify-between bg-panel">
                <span className="font-mono text-[10.5px] text-faint uppercase tracking-[0.12em]">
                  $ try this
                </span>
                <button
                  type="button"
                  onClick={copyCurl}
                  disabled={!id}
                  className="font-mono text-[11px] text-signal-red hover:text-foreground transition-colors disabled:opacity-30"
                >
                  {copied ? "copied ✓" : "copy"}
                </button>
              </div>
              <pre className="p-4 font-mono text-[12px] leading-relaxed text-foreground whitespace-pre-wrap break-all">
                {error
                  ? `# ${error}\n# refresh to try again`
                  : id
                    ? curlSnippet
                    : "# minting your inbox…"}
              </pre>
            </div>

            <div className="mt-6 flex items-center gap-3">
              <Link
                to="/register"
                onClick={() =>
                  trackCtaClick("playground", { target: "register" })
                }
              >
                <Button size="lg">Already convinced? Start free →</Button>
              </Link>
              <span className="font-mono text-[11px] text-faint">
                expires in 1h · ephemeral · for real
              </span>
            </div>
          </div>

          <div className="lg:pl-4">
            <div className="rounded-xl border border-rule bg-card-paper overflow-hidden shadow-sm">
              <div className="px-4 py-3 border-b border-rule-soft flex items-center justify-between bg-panel">
                <div className="flex items-center gap-2">
                  <span className="size-1.5 rounded-sm bg-signal-ok animate-pulse" />
                  <span className="text-eyebrow">Live inbox · this page</span>
                </div>
                <span className="font-mono text-[10px] text-faint">
                  {requests.length}/{MAX_VISIBLE}
                </span>
              </div>
              <div className="divide-y divide-rule-soft">
                {requests.length === 0 ? (
                  <div className="px-5 py-12 text-center">
                    <div className="font-serif italic text-muted-foreground text-sm">
                      Waiting for your first request…
                    </div>
                    <div className="mt-2 font-mono text-[10.5px] text-faint">
                      copy the curl on the left, paste into your terminal
                    </div>
                  </div>
                ) : (
                  requests.map((r) => <PlaygroundRequestRow key={r.id} r={r} />)
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function PlaygroundRequestRow({ r }: { r: CapturedRequest }) {
  const time = new Date(r.timestamp).toLocaleTimeString();
  const bodyText =
    typeof r.body === "string"
      ? r.body
      : r.body === null || r.body === undefined
        ? "(empty)"
        : JSON.stringify(r.body, null, 2);
  return (
    <div className="px-4 py-3">
      <div className="flex items-center justify-between mb-1.5">
        <span className="font-mono text-[10px] font-semibold text-signal-red">
          {r.method}
        </span>
        <span className="font-mono text-[10px] text-faint">{time}</span>
      </div>
      <pre className="font-mono text-[11px] leading-relaxed text-muted-foreground whitespace-pre-wrap break-all max-h-32 overflow-auto">
        {bodyText}
      </pre>
    </div>
  );
}
